import { CanvasDrawing } from './CanvasDrawing.ts';
import { ComponentsPanel } from './ComponentsPanel.ts';
import { DropCanvas } from './DropCanvas.ts';
import { callClaude, buildTextMessage, buildImageMessage } from '../api/claude.ts';
import { parseFigmaFileKey, fetchFigmaFrames, fetchFigmaFrameAsBase64 } from '../api/figma.ts';
import { escapeHtml, extractHTML, bytesToKB } from '../utils/html.ts';
import type { AppState, Message, TabName, ViewMode, DrawTool } from '../types/index.ts';

const API_KEY_STORAGE = 'awb_api_key';

export class App {
  private state: AppState = {
    generatedHTML: '',
    conversationHistory: [],
    currentView: 'preview',
    currentTab: 'prompt',
    isLoading: false,
    sessionTokensUsed: 0,
    lastInputTokens: 0,
  };

  private canvas: CanvasDrawing | null = null;
  private dropCanvas: DropCanvas | null = null;
  private stepInterval: ReturnType<typeof setInterval> | null = null;
  private pendingAction: (() => void) | null = null;
  private figmaState: { fileKey: string; frameId: string; frameName: string } | null = null;

  init(): void {
    this.setupApiKeyModal();
    this.setupTabs();
    this.setupPrompt();
    this.setupDraw();
    this.setupComponents();
    this.setupFigmaImport();
    this.setupPreview();
    this.setupExport();
    this.setupPanelResizer();
  }

  // ── API KEY MODAL ──────────────────────────────────────────

  private setupApiKeyModal(): void {
    const modal = document.getElementById('api-key-modal')!;
    const input = document.getElementById('api-key-input') as HTMLInputElement;
    const saveBtn = document.getElementById('api-key-save')!;

    modal.classList.add('hidden');

    (saveBtn as HTMLButtonElement).disabled = true;

    input.addEventListener('input', () => {
      (saveBtn as HTMLButtonElement).disabled = input.value.trim().length === 0;
      input.style.borderColor = '';
    });

    saveBtn.addEventListener('click', () => {
      const key = input.value.trim();
      if (!key.startsWith('sk-')) {
        input.style.borderColor = 'var(--red)';
        return;
      }
      localStorage.setItem(API_KEY_STORAGE, key);
      modal.classList.add('hidden');
      const action = this.pendingAction;
      this.pendingAction = null;
      action?.();
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') saveBtn.click();
      input.style.borderColor = '';
    });
  }

  private requireApiKey(action: () => void): void {
    if (this.getApiKey()) {
      action();
    } else {
      this.pendingAction = action;
      document.getElementById('api-key-modal')!.classList.remove('hidden');
    }
  }

  private getApiKey(): string {
    return import.meta.env.VITE_ANTHROPIC_API_KEY || localStorage.getItem(API_KEY_STORAGE) || '';
  }

  // ── TABS ──────────────────────────────────────────────────

  private setupTabs(): void {
    document.querySelectorAll<HTMLElement>('.tab').forEach((tab) => {
      tab.addEventListener('click', () => {
        const name = tab.dataset.tab as TabName;
        this.switchTab(name);
      });
    });
  }

  private switchTab(name: TabName): void {
    this.state.currentTab = name;
    document.querySelectorAll('.tab').forEach((t) =>
      t.classList.toggle('active', (t as HTMLElement).dataset.tab === name)
    );
    document.querySelectorAll('.tab-content').forEach((c) =>
      c.classList.remove('active')
    );
    document.getElementById(`tab-${name}`)!.classList.add('active');

    // Toggle drop canvas vs normal preview panel
    const isComponents = name === 'components';
    const isFigma = name === 'figma';
    document.getElementById('drop-canvas-panel')!.style.display = isComponents ? 'flex' : 'none';
    document.getElementById('preview-container')!.style.display = (isComponents || isFigma) ? 'none' : 'block';

    if (name === 'draw') requestAnimationFrame(() => requestAnimationFrame(() => this.initCanvas()));
  }

  // ── PROMPT ────────────────────────────────────────────────

  private setupPrompt(): void {
    const input = document.getElementById('prompt-input') as HTMLTextAreaElement;
    const sendBtn = document.getElementById('btn-send')!;
    const generateBtn = document.getElementById('btn-generate')!;

    sendBtn.addEventListener('click', () => this.generate());
    generateBtn.addEventListener('click', () => this.generate());

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.generate();
      }
    });

    input.addEventListener('input', () => this.autoResize(input));

    document.querySelectorAll<HTMLElement>('.suggestion').forEach((chip) => {
      chip.addEventListener('click', () => {
        input.value = chip.dataset.value ?? chip.textContent ?? '';
        this.autoResize(input);
        document.getElementById('suggestions')!.style.display = 'none';
      });
    });
  }

  private autoResize(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }

  private generate(): void {
    const input = document.getElementById('prompt-input') as HTMLTextAreaElement;
    const prompt = input.value.trim();
    if (!prompt || this.state.isLoading) return;

    this.requireApiKey(() => {
      this.addMessage('user', prompt);
      input.value = '';
      input.style.height = 'auto';
      document.getElementById('suggestions')!.style.display = 'none';
      this.runGeneration(buildTextMessage(prompt));
    });
  }

  private addMessage(role: 'user' | 'ai', text: string): void {
    const list = document.getElementById('message-list')!;
    const div = document.createElement('div');
    div.className = `msg ${role}`;
    const avatar = role === 'user' ? 'You' : 'AW';
    const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    div.innerHTML = `
      <div class="msg-avatar">${avatar}</div>
      <div>
        <div class="msg-bubble">${escapeHtml(text)}</div>
        <div class="msg-time">${now}</div>
      </div>
    `;
    list.appendChild(div);
    list.parentElement!.scrollTop = list.parentElement!.scrollHeight;
  }

  // ── DRAW ──────────────────────────────────────────────────

  private setupDraw(): void {
    document.querySelectorAll<HTMLElement>('.tool-btn[data-tool]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tool = btn.dataset.tool as DrawTool;
        this.canvas?.setTool(tool);
        document.querySelectorAll('.tool-btn[data-tool]').forEach((b) =>
          b.classList.remove('active')
        );
        btn.classList.add('active');
      });
    });

    document.querySelectorAll<HTMLElement>('.color-dot').forEach((dot) => {
      dot.addEventListener('click', () => {
        const color = dot.dataset.color!;
        this.canvas?.setColor(color);
        document.querySelectorAll('.color-dot').forEach((d) => d.classList.remove('active'));
        dot.classList.add('active');
      });
    });

    document.getElementById('btn-clear-canvas')!.addEventListener('click', () => {
      this.canvas?.clear();
    });

    document.getElementById('btn-build-sketch')!.addEventListener('click', () => {
      this.generateFromCanvas();
    });
  }

  private initCanvas(): void {
    if (this.canvas) return;
    const el = document.getElementById('draw-canvas') as HTMLCanvasElement;
    this.canvas = new CanvasDrawing(el);
  }

  private generateFromCanvas(): void {
    if (!this.canvas) {
      alert('Please draw something first.');
      return;
    }
    const dataURL = this.canvas.toDataURL();
    const base64 = dataURL.split(',')[1];
    this.requireApiKey(() => {
      this.addMessage('user', '[Sketch attached] Build a website based on my wireframe sketch.');
      this.runGeneration(buildImageMessage(base64));
    });
  }

  // ── GENERATION ────────────────────────────────────────────

  private async runGeneration(userMessage: Message): Promise<void> {
    this.state.conversationHistory.push(userMessage);
    this.setLoading(true);

    try {
      this.animateLoadingSteps();
      const result = await callClaude(this.getApiKey(), this.state.conversationHistory);

      this.state.conversationHistory.push({ role: 'assistant', content: result.content });
      this.state.sessionTokensUsed += result.inputTokens + result.outputTokens;
      this.state.lastInputTokens = result.inputTokens;
      this.updateTokenCounter();

      const clean = extractHTML(result.content);
      this.state.generatedHTML = clean;
      this.renderPreview(clean);

      const lineCount = clean.split('\n').length;
      this.addMessage('ai', `Done! Generated ${lineCount} lines of HTML. You can ask me to make changes — just describe what you want to adjust.`);

      document.getElementById('btn-copy')!.style.display = 'flex';
      document.getElementById('btn-download')!.style.display = 'flex';
      const badge = document.getElementById('size-badge')!;
      badge.style.display = 'block';
      badge.textContent = `${bytesToKB(clean)} KB`;

      this.setStatus(`Generated · ${new Date().toLocaleTimeString()}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.addMessage('ai', `Something went wrong: ${message}. Please try again.`);
      this.setStatus('Error', true);
    }

    this.setLoading(false);
  }

  // ── COMPONENTS ────────────────────────────────────────────

  private setupComponents(): void {
    this.dropCanvas = new DropCanvas();

    new ComponentsPanel('component-palette', (comp) => {
      this.dropCanvas!.add(comp);
    });

    document.getElementById('btn-build-components')!.addEventListener('click', () => {
      this.generateFromComponents();
    });

    document.getElementById('btn-clear-components')!.addEventListener('click', () => {
      this.dropCanvas!.clear();
    });
  }

  private generateFromComponents(): void {
    if (!this.dropCanvas?.hasItems()) {
      alert('Add at least one component to the canvas first.');
      return;
    }
    const prompt = this.dropCanvas.getLayoutPrompt();
    this.requireApiKey(() => {
      this.addMessage('user', '[Components layout] ' + prompt.split('\n')[0]);
      this.switchTab('prompt');
      this.runGeneration(buildTextMessage(prompt));
    });
  }

  // ── FIGMA IMPORT ──────────────────────────────────────────

  private setupFigmaImport(): void {
    const FIGMA_TOKEN_KEY = 'awb_figma_token';
    const urlInput = document.getElementById('figma-url-input') as HTMLInputElement;
    const tokenInput = document.getElementById('figma-token-input') as HTMLInputElement;
    const fetchBtn = document.getElementById('btn-figma-fetch')!;
    const buildBtn = document.getElementById('btn-build-figma')!;

    const stored = localStorage.getItem(FIGMA_TOKEN_KEY);
    if (stored) tokenInput.value = stored;

    fetchBtn.addEventListener('click', async () => {
      const url = urlInput.value.trim();
      const token = tokenInput.value.trim();
      if (!url || !token) {
        urlInput.style.borderColor = url ? '' : 'var(--red)';
        tokenInput.style.borderColor = token ? '' : 'var(--red)';
        return;
      }
      urlInput.style.borderColor = '';
      tokenInput.style.borderColor = '';

      const fileKey = parseFigmaFileKey(url);
      if (!fileKey) {
        urlInput.style.borderColor = 'var(--red)';
        return;
      }

      localStorage.setItem(FIGMA_TOKEN_KEY, token);
      fetchBtn.textContent = 'Fetching…';
      (fetchBtn as HTMLButtonElement).disabled = true;

      try {
        const frames = await fetchFigmaFrames(token, fileKey);
        this.renderFigmaFrames(frames, fileKey, token);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        this.showFigmaError(msg);
      } finally {
        fetchBtn.textContent = 'Fetch frames';
        (fetchBtn as HTMLButtonElement).disabled = false;
      }
    });

    buildBtn.addEventListener('click', () => {
      if (!this.figmaState) return;
      const { fileKey, frameId, frameName } = this.figmaState;
      const token = tokenInput.value.trim();
      this.requireApiKey(async () => {
        buildBtn.textContent = 'Exporting…';
        (buildBtn as HTMLButtonElement).disabled = true;
        try {
          const base64 = await fetchFigmaFrameAsBase64(token, fileKey, frameId);
          this.addMessage('user', `[Figma: ${frameName}] Build a website matching this Figma design.`);
          this.switchTab('prompt');
          this.runGeneration(buildImageMessage(base64, `Build a pixel-accurate website from this Figma design frame called "${frameName}". Match the layout, typography, colours, spacing, and components as closely as possible in a single self-contained HTML file.`));
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          this.showFigmaError(msg);
        } finally {
          buildBtn.textContent = 'Build from design';
          (buildBtn as HTMLButtonElement).disabled = false;
        }
      });
    });
  }

  private renderFigmaFrames(frames: { id: string; name: string }[], fileKey: string, _token: string): void {
    const container = document.getElementById('figma-frames')!;
    const buildRow = document.getElementById('figma-build-row')!;
    container.innerHTML = '';

    if (frames.length === 0) {
      container.innerHTML = '<span class="figma-empty">No frames found on the first page.</span>';
      return;
    }

    frames.forEach((frame) => {
      const chip = document.createElement('div');
      chip.className = 'figma-frame-chip';
      chip.textContent = frame.name;
      chip.addEventListener('click', () => {
        container.querySelectorAll('.figma-frame-chip').forEach((c) => c.classList.remove('active'));
        chip.classList.add('active');
        this.figmaState = { fileKey, frameId: frame.id, frameName: frame.name };
        document.getElementById('figma-selected-name')!.textContent = frame.name;
        buildRow.style.display = 'flex';
      });
      container.appendChild(chip);
    });
  }

  private showFigmaError(msg: string): void {
    const container = document.getElementById('figma-frames')!;
    container.innerHTML = `<span class="figma-error">${msg}</span>`;
  }

  // ── PREVIEW ───────────────────────────────────────────────

  private setupPreview(): void {
    document.querySelectorAll<HTMLElement>('.view-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const view = btn.dataset.view as ViewMode;
        this.setView(view);
      });
    });
  }

  private renderPreview(html: string): void {
    document.getElementById('empty-state')!.classList.add('hidden');
    (document.getElementById('preview-frame') as HTMLIFrameElement).srcdoc = html;
    document.getElementById('url-text')!.textContent = 'preview://generated-site';
    document.getElementById('code-output')!.textContent = html;
  }

  private setView(view: ViewMode): void {
    this.state.currentView = view;
    document.querySelectorAll('.view-btn').forEach((b) =>
      b.classList.toggle('active', (b as HTMLElement).dataset.view === view)
    );
    const frame = document.getElementById('preview-frame')!;
    const code = document.getElementById('code-panel')!;
    frame.style.display = view === 'preview' ? 'block' : 'none';
    code.classList.toggle('active', view === 'code');
  }

  // ── EXPORT ────────────────────────────────────────────────

  private setupExport(): void {
    document.getElementById('btn-download')!.addEventListener('click', () => {
      if (!this.state.generatedHTML) return;
      const blob = new Blob([this.state.generatedHTML], { type: 'text/html' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'my-site.html';
      a.click();
      URL.revokeObjectURL(a.href);
    });

    document.getElementById('btn-copy')!.addEventListener('click', async () => {
      if (!this.state.generatedHTML) return;
      await navigator.clipboard.writeText(this.state.generatedHTML);
      const btn = document.getElementById('btn-copy')!;
      const orig = btn.innerHTML;
      btn.innerHTML = '✓ Copied!';
      setTimeout(() => (btn.innerHTML = orig), 1500);
    });
  }

  // ── LOADING ───────────────────────────────────────────────

  private setLoading(on: boolean): void {
    this.state.isLoading = on;
    document.getElementById('loading')!.classList.toggle('active', on);
    (document.getElementById('btn-generate') as HTMLButtonElement).disabled = on;

    if (!on) {
      if (this.stepInterval) clearInterval(this.stepInterval);
      [1, 2, 3, 4].forEach((i) =>
        document.getElementById(`step-${i}`)!.classList.remove('done')
      );
    }
  }

  private animateLoadingSteps(): void {
    let step = 1;
    this.stepInterval = setInterval(() => {
      if (step <= 4) {
        document.getElementById(`step-${step}`)!.classList.add('done');
        step++;
      } else {
        if (this.stepInterval) clearInterval(this.stepInterval);
      }
    }, 900);
  }

  private setupPanelResizer(): void {
    const resizer = document.getElementById('panel-resizer')!;
    const leftPanel = document.querySelector('.left-panel') as HTMLElement;
    let startX = 0;
    let startW = 0;

    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - startX;
      const newW = Math.min(Math.max(startW + dx, 260), window.innerWidth * 0.7);
      leftPanel.style.width = newW + 'px';
    };

    const onUp = () => {
      resizer.classList.remove('dragging');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    resizer.addEventListener('mousedown', (e) => {
      startX = e.clientX;
      startW = leftPanel.offsetWidth;
      resizer.classList.add('dragging');
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    });
  }

  private setStatus(msg: string, error = false): void {
    document.getElementById('status-text')!.textContent = msg;
    const dot = document.getElementById('status-dot')!;
    dot.style.background = error ? 'var(--red)' : 'var(--green)';
  }

  private updateTokenCounter(): void {
    const CONTEXT_LIMIT = 200_000;
    const used = this.state.sessionTokensUsed;
    const left = Math.max(0, CONTEXT_LIMIT - this.state.lastInputTokens);
    const pct = Math.min(100, (this.state.lastInputTokens / CONTEXT_LIMIT) * 100);

    const fmt = (n: number) =>
      n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

    const el = document.getElementById('token-counter')!;
    el.style.display = 'flex';
    document.getElementById('token-used')!.textContent = fmt(used);
    document.getElementById('token-left')!.textContent = fmt(left);
    (document.getElementById('token-bar-fill') as HTMLElement).style.width = `${pct}%`;
  }
}
